<template>
  <div class="animated fadeIn col-md-12">
    <b-row>
      <b-col cols="12">
        <b-card>
          <div class="text-center" v-show="loadingItem">
            <b-spinner variant="primary" label="Text Centered"></b-spinner>
          </div>
          <div slot="header">
            ایجاد
            <strong v-text="$lang.user"></strong>
          </div>
          <b-alert show variant="info" class="mb-0">
            <i class="fas fa-info-circle fa-lg mr-1" />
            <b v-html="$lang.user_add_top_tooltip" />
          </b-alert>
          <b-form @submit.prevent="save" v-show="!loadingItem">
            <b-form-group
              :label="$lang.person"
              label-for="person_selected"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.person"
                :rules="$v.person_selected.$params"
              />
              <selectAsync
                :options="person_options"
                v-model="$v.person_selected.$model"
                :asyncFind="getPersonCombo"
                label="text"
                id="value"
                :isValid="
                  $v.person_selected.$dirty ? !$v.person_selected.$error : null
                "
                :loading="person_loading"
              />
              <b-form-invalid-feedback id="person_selected-feedback"
                >{{ $lang.error_required | replace($lang.person) }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              :label="$lang.mobile"
              label-for="mobile"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.mobile"
                :rules="$v.mobile.$params"
              />
              <b-form-input
                id="mobile"
                type="text"
                autocomplete="off"
                :state="$v.mobile.$dirty ? !$v.mobile.$error : null"
                v-model="$v.mobile.$model"
              ></b-form-input>

              <b-form-invalid-feedback id="mobile-feedback"
                >{{ $lang.error_numeric | replace($lang.mobile) }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              :label="$lang.email"
              label-for="email"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.email"
                :rules="$v.email.$params"
              />
              <b-form-input
                id="email"
                type="text"
                autocomplete="off"
                :state="$v.email.$dirty ? !$v.email.$error : null"
                v-model="$v.email.$model"
              ></b-form-input>
              <b-form-invalid-feedback id="email-feedback"
                >{{ $lang.error_required | replace($lang.email) }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              :label="$lang.username"
              label-for="username"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.username"
                :rules="$v.username.$params"
              />
              <b-form-input
                id="username"
                type="text"
                autocomplete="off"
                :state="$v.username.$dirty ? !$v.username.$error : null"
                v-model="$v.username.$model"
              ></b-form-input>

              <b-form-invalid-feedback id="username-feedback"
                >{{ $lang.error_numeric | replace($lang.username) }}
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group
              :label="$lang.password"
              label-for="password"
              :label-cols="12"
            >
              <requiredComponent
                slot="label"
                :label="$lang.password"
                :rules="$v.password.$params"
              />
              <b-input-group>
                <b-form-input
                  id="password"
                  :type="show_password ? 'text' : 'password'"
                  :autocomplete="$lang.password"
                  :state="$v.password.$dirty ? !$v.password.$error : null"
                  v-model="$v.password.$model"
                ></b-form-input>
                <b-input-group-text
                  @click.prevent="toggleShowNewVisible"
                  style="cursor: pointer"
                >
                  <a href="#">
                    <i
                      :class="show_password ? 'fa fa-eye' : 'fa fa-eye-slash'"
                      aria-hidden="true"
                    ></i>
                  </a>
                </b-input-group-text>
                <b-form-invalid-feedback id="password-feedback">
                  {{
                    $lang.error_required | replace($lang.password)
                  }}</b-form-invalid-feedback
                >
              </b-input-group>
              <small class="form-text text-muted text-left">{{
                $lang.password_template
              }}</small>
            </b-form-group>

            <b-form-group
              :label="$lang.password_confirmation"
              label-for="password_confirmation"
              :label-cols="12"
            >
              <requiredComponent
                slot="label"
                :label="$lang.password_confirmation"
                :rules="$v.password_confirmation.$params"
              />
              <b-input-group>
                <b-form-input
                  id="password_confirmation"
                  :type="show_current_password ? 'text' : 'password'"
                  :autocomplete="$lang.password_confirmation"
                  :state="
                    $v.password_confirmation.$dirty
                      ? !$v.password_confirmation.$error
                      : null
                  "
                  v-model="$v.password_confirmation.$model"
                ></b-form-input>
                <b-input-group-text
                  @click.prevent="toggleShowConfirmVisible"
                  style="cursor: pointer"
                >
                  <a href="#">
                    <i
                      :class="
                        show_current_password ? 'fa fa-eye' : 'fa fa-eye-slash'
                      "
                      aria-hidden="true"
                    ></i>
                  </a>
                </b-input-group-text>
                <b-form-invalid-feedback id="password_confirmation-feedback">
                  {{
                    $lang.error_required | replace($lang.password_confirmation)
                  }}</b-form-invalid-feedback
                >
              </b-input-group>
              <small class="form-text text-muted text-left">{{
                $lang.password_template
              }}</small>
            </b-form-group>

            <b-form-group
              :label="$lang.expire_at"
              label-for="expire_at"
              :label-cols="$config.form.labelCol"
            >
              <requiredComponent
                slot="label"
                :label="$lang.expire_at"
                :rules="$v.expire_at.$params"
                :showInfo="true"
                :titleText="$lang.expire_at_tooltip"
              />
              <date-picker
                v-model="$v.expire_at.$model"
                :state="$v.expire_at.$dirty ? !$v.expire_at.$error : null"
                format="YYYY/MM/DD"
                display-format="dddd jDD jMMMM jYYYY"
                min="2020/09/27"
                inputReq="true"
                :clearable="true"
                :class="
                  $v.expire_at.$dirty
                    ? $v.expire_at.$error
                      ? 'invalid-date'
                      : 'valid-date'
                    : null
                "
              >
              </date-picker>
              <b-form-invalid-feedback
                :style="{ display: $v.expire_at.$error ? 'block' : 'none' }"
                id="gender_id-feedback"
              >
                {{
                  $lang.error_required | replace($lang.expire_at)
                }}</b-form-invalid-feedback
              >
            </b-form-group>

            <b-form-group v-if="isActiveSmsPanel()">
              <b-form-checkbox
                id="checkbox-1"
                v-model="userSendInfo"
                name="checkbox-1"
                value="1"
                unchecked-value="0"
              >
                {{ $lang.sendInfoUser
                }}<i
                  class="fa fa-info-circle fa-lg text-info mx-1"
                  v-b-tooltip.hover
                  :title="$lang.sendInfoUser_tooltip"
                ></i
              ></b-form-checkbox>
            </b-form-group>
            <div slot="footer" class="form-group form-btn">
              <b-button type="submit" size="md" variant="success">
                {{ $lang.submitButton }}
                <i class="fa fa-check"></i>
              </b-button>
              <b-button
                type="reset"
                class="ml-2"
                size="md"
                variant="danger"
                @click="handleBack"
              >
                {{ $lang.cancelButton }}
                <i class="fa fa-ban"></i>
              </b-button>
            </div>
          </b-form>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { required, numeric } from "vuelidate/lib/validators";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import { pathOr } from "ramda";
import { isActiveSmsPanel } from "@/shared/utils";

export default {
  name: "add-user",
  validations: {
    person_selected: {
      required,
    },
    mobile: {
      numeric,
      required,
    },
    email: {},
    expire_at: {},
    username: {
      required,
    },
    password: {
      required,
    },
    password_confirmation: {
      required,
    },
  },
  components: {
    datePicker: VuePersianDatetimePicker,
  },
  props: {
    isModal: {
      type: Boolean,
      default: false,
    },
    backUrl: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loadingItem: false,
      expire_at: null,
      mobile: "",
      email: "",
      username: "",
      password: "",
      password_confirmation: "",
      person_options: [],
      person_loading: false,
      person_selected: null,
      userSendInfo: "0",
      show_current_password: false,
      show_password: false,
    };
  },
  watch: {
    person_selected(value) {
      const mobile = pathOr([], ["contacts"], value).find(
        (item) => pathOr(null, ["kind_id"], item) === 1
      );
      const email = pathOr([], ["contacts"], value).find(
        (item) => pathOr(null, ["kind_id"], item) === 3
      );
      this.mobile = pathOr(null, ["content"], mobile);
      this.email = pathOr(null, ["content"], email);
    },
  },
  methods: {
    pathOr,
    isActiveSmsPanel,
    toggleShowConfirmVisible() {
      this.show_current_password = !this.show_current_password;
    },
    toggleShowNewVisible() {
      this.show_password = !this.show_password;
    },
    handleBack() {
      if (this.isModal) {
        this.$emit("modal-end-up");
      } else {
        window.history.length > 1
          ? this.$router.go(-1)
          : this.$router.push("/");
      }
    },
    clear() {
      this.person_selected = "";
      this.mobile = "";
      this.email = "";
      this.password = "";
      this.password_confirmation = "";
    },
    afterSave() {
      if (this.backUrl) {
        this.$route.push(this.backUrl);
      } else {
        this.handleBack();
      }
    },
    async getPersonCombo(query) {
      if (!query || query.length < 2) {
        return;
      }
      if (query.length > 2) {
        this.person_options = [];
      }

      try {
        this.person_loading = true;
        const url =
          this.$config.api_url +
          this.$config.profile.user_person_combo.replace("{0}", query);
        const response = await this.$httpService.get(url);

        this.person_options = response.data.data.map((x) => {
          return {
            text: `${pathOr("", ["person", "name"], x)} ${pathOr(
              "",
              ["person", "family"],
              x
            )} - ${pathOr("", ["person", "national_code"], x)}`,
            value: pathOr("", ["person", "code"], x),
            contacts: pathOr("", ["person", "contacts"], x),
          };
        });
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.person_loading = false;
      }
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.loadingItem = true;
        const url = this.$config.api_url + this.$config.resources.user;
        const payload = {
          person_code: pathOr(null, ["value"], this.person_selected),
          mobile: this.mobile,
          email: this.email,
          username: this.username,
          password: this.password,
          password_confirmation: this.password_confirmation,
          send_user_info: +this.userSendInfo,
          expire_at: this.expire_at,
        };
        const response = await this.$httpService.post(url, payload);
        this.$notify.success(response.data.message);
        this.afterSave();
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.loadingItem = false;
      }
    },
  },
};
</script>

